/**
 * Pure utility functions for notification data transformations.
 * Centralizes all repeated logic that was duplicated across
 * notifications.page.ts and web-notifications.component.ts.
 */

/**
 * Escapes HTML special characters to prevent XSS.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Normalizes the notification type from various possible locations
 * in the notification payload to a number or null.
 */
export function normalizeNotificationType(notification: any): number | null {
  if (!notification) return null;

  const rawType: unknown =
    notification?.data?.type ??
    notification?.type ??
    notification?.notification_type;

  if (rawType == null) return null;

  const trimmed: string = typeof rawType === 'string' ? rawType.trim() : String(rawType);
  const parsed: number = typeof rawType === 'number' ? rawType : parseInt(trimmed, 10);

  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Builds rich HTML for recovery notifications (type 6).
 * First line becomes bold, subsequent lines get bold values after colons.
 */
export function buildRecoveryNotificationHtml(body: string): string {
  const normalized: string = String(body || '').replace(/\r\n/g, '\n').trim();
  if (!normalized) return '';

  const lines: string[] = normalized
    .split('\n')
    .map((line: string) => line.trim())
    .filter((line: string) => line.length > 0);

  return lines
    .map((line: string, index: number) => {
      const escapedLine: string = escapeHtml(line);
      if (index === 0) {
        return `<strong>${escapedLine}</strong>`;
      }
      return escapedLine.replace(/:\s*([^.<]+?)(?=\.|$)/, ': <strong>$1</strong>');
    })
    .join('<br>');
}

/**
 * Extracts the conversation ID from a notification payload.
 * Checks data.id first, then mapped originalData.data.id, and only
 * falls back to the notification's own id when no conversation payload exists.
 */
export function extractConversationId(notification: any): number | null {
  if (!notification) return null;

  const rawId: unknown =
    notification?.data?.id ??
    notification?.originalData?.data?.id ??
    notification?.id ??
    null;
  const parsedId: number = Number(rawId);

  return Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;
}

/**
 * Extracts the navigation route from a notification payload.
 * Checks multiple possible locations in order of precedence.
 */
export function extractNotificationRoute(notification: any): string | null {
  if (!notification) return null;

  const route: unknown =
    notification?.route ??
    notification?.data?.route ??
    notification?.originalData?.route ??
    notification?.originalData?.data?.route;

  return typeof route === 'string' && route.trim().length > 0 ? route.trim() : null;
}

/**
 * Returns the original API object from a mapped notification,
 * or the notification itself if no originalData exists.
 */
export function getNotificationSource(notification: any): any {
  if (notification == null) return notification;
  return notification?.originalData || notification;
}

/**
 * Normalizes the notification read state across the different aliases used
 * by the API payloads and view models.
 */
export function withNormalizedNotificationReadState<T extends Record<string, any>>(notification: T): T & {
  viewed: boolean;
  is_read: boolean;
  new: boolean;
} {
  const isRead: boolean = !!(notification?.viewed ?? notification?.is_read);

  return {
    ...notification,
    viewed: isRead,
    is_read: isRead,
    new: !isRead,
  };
}

/**
 * Keeps local notification aliases synchronized after the read state changes.
 */
export function syncNotificationReadState(notification: any): void {
  if (!notification) return;

  notification.viewed = true;
  notification.is_read = true;
  notification.new = false;

  if (notification.originalData && typeof notification.originalData === 'object') {
    notification.originalData.viewed = true;
    notification.originalData.is_read = true;
  }
}

/**
 * Checks if a notification is a proposal notification
 * (type 4 with suppress_open_action flag from the owner's first free message).
 */
export function isProposalNotification(notification: any): boolean {
  if (!notification?.data) return false;
  return notification.data.type === 4 && notification.data.suppress_open_action === true;
}

/**
 * Returns the appropriate translation key for the notification action button.
 */
export function getNotificationActionKey(notification: any): string {
  const type: number | null = normalizeNotificationType(notification);
  return type === 6 ? 'NOTIFICATIONS.VIEW_RECOVERY_OPTIONS' : 'NOTIFICATIONS.ACCESS';
}
